import React from 'react';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../redux/actions'; //Import your actions
import TextareaAutosize from 'react-textarea-autosize';
import ReportTestHistory from './ReportTestHistory.js'
import '../../styles/report.scss';

import { Checkbox } from 'react-materialize';

import EditIcon from '@mui/icons-material/Edit';
import TimelineIcon from '@mui/icons-material/Timeline';
import ChatIcon from '@mui/icons-material/Chat';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import LoopIcon from '@mui/icons-material/Loop';
import FxIcon from '../../components/FxIcon';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CalcSourcesEditor from './CalcSourcesEditor';
import { evaluateFormula } from '../../utils/formula';

// What the server last saved as typed for an input. A value it filled in from
// the input's standard value ('defaulted') wasn't typed.
const savedEntry = (input) => (input.defaulted ? '' : input.value ?? '');

// What's typed for an input on this test, blank when it's left to the standard value.
const typedEntry = (test, input) => test.inputs?.[input.key] ?? savedEntry(input);

// The current value of each input of a calculated test, by input key:
// what's typed on the test (or its standard value while nothing is), the
// linked test's reading on this report as it stands (so the result follows it
// while it's being typed), or the previous report's reading the server sent.
function calcInputValues(test, report) {
  const values = {};
  (test?.calc?.inputs || []).forEach((input) => {
    if (input.source === 'entered') {
      const typed = typedEntry(test, input);
      values[input.key] = `${typed}`.trim() === '' && input.default != null ? input.default : typed;
    } else if (input.source === 'test') {
      const linked = Object.values(report?.components?.[input.component_id]?.tests || {})
        .find((t) => !t.deleted && `${t.testId}` === `${input.test_field_id}`);
      values[input.key] = linked ? linked.result ?? null : null;
    } else {
      values[input.key] = input.value ?? null;
    }
  });
  return values;
}

class ReportTest extends React.Component {

  constructor() {
    super()

    this.state = {
      showNoteInput: false,
      showHistory: false,
      historyMounted: false,
      showDelete: false,
      showLimitsEditInputs: false,
      showSources: false,
      rangeIndicator: null,
      optionsShown: false,
      DEBUG_chekd: undefined,
      editing: false,
      valueChanged: false,
      showSavedIndicator: false,
    }
  }

  componentDidMount() {
    let canvas = document.getElementById(`history-${this.props.test.testId}`);
    if (canvas && !canvas.classList.contains('drawn')) {
      this.drawHistoryChart(canvas);
      canvas.classList.add('drawn');
    }
    this.calcRangeIndicator();
    document.addEventListener('report-test-activate', this.handleOtherTestActivate);
  }

  componentWillUnmount() {
    document.removeEventListener('report-test-activate', this.handleOtherTestActivate);
  }

  handleOtherTestActivate = (event) => {
    if (event.detail?.id === this.props.test.id) return;
    this.setState({
      optionsShown: false,
      showNoteInput: false,
      showHistory: false,
      showLimitsEditInputs: false,
      showSources: false,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.test.synced !== this.props.test.synced && this.props.test.synced) {
      this.setState({ showSavedIndicator: true });
      setTimeout(() => {
        this.setState({ showSavedIndicator: false });
      }, 2000);
    }

    // A calculated result moves when any of its inputs does.
    if (this.isCalculated() && (prevProps.calcKey !== this.props.calcKey || prevProps.test.calc !== this.props.test.calc)) {
      this.calcRangeIndicator(this.calcResult());
    }
  }

  // Calculated tests aren't typed in: the result comes from a formula over
  // the inputs in test.calc. The service notebook (saveNode) doesn't have them.
  isCalculated() {
    return this.props.test.type === 'calculated' && !this.props.saveNode;
  }

  // The result as the inputs stand right now. The server works out the same
  // number when the report saves.
  calcResult() {
    const calc = this.props.test.calc;
    if (!calc) return this.props.test.result ?? null;
    return evaluateFormula(calc.formula, calcInputValues(this.props.test, this.props.report), calc.decimals ?? 2);
  }

  setEnteredInput(key, value, storage) {
    return this.persist({inputs: {...(this.props.test.inputs || {}), [key]: value}}, storage);
  }

  validateResult(result) { // make sure value entered in result is a valid number
    console.log('validating result', result);
    if (typeof result === 'string') {
      if (result.indexOf('-') !== -1) {
        // Move all '-' to the beginning and remove any subsequent '-'
        let dashCount = (result.match(/-/g) || []).length;
        result = result.replace(/-/g, '');
        if (dashCount > 0) {
          result = '-' + result;
        }
      }
      return result?.replace(/[^0-9.\-\/]/g,'');
    } else if (typeof result === 'boolean') {
      return result;
    }
  }

  activateTest() {
    document.dispatchEvent(new CustomEvent('report-test-activate', {
      detail: { id: this.props.test.id },
    }));
  }

  revealOptions() {
    this.activateTest();
    this.setState({ optionsShown: true });
  }

  // Trend, note, and limits share one slot — opening one closes the others.
  showOption(option) {
    this.activateTest();
    const closing = this.state[option];
    const next = {
      optionsShown: true,
      showNoteInput: false,
      showHistory: false,
      showLimitsEditInputs: false,
      showSources: false,
    };
    if (!closing) next[option] = true;

    if (option === 'showHistory' && next.showHistory && !this.state.historyMounted) {
      this.setState({ ...next, showHistory: false, historyMounted: true }, () => {
        requestAnimationFrame(() => this.setState({ showHistory: true }));
      });
      return;
    }

    this.setState(next);
  }

  isNodeExpanded() {
    const {
      editing,
      showNoteInput,
      showHistory,
      showLimitsEditInputs,
      showSources,
      optionsShown,
    } = this.state;

    return (
      editing
      || showNoteInput
      || showHistory
      || showLimitsEditInputs
      || showSources
      || optionsShown
    );
  }

  focusInput() {
    if (this.state.showDelete) {return false}
    const result = document.querySelector(`input.result[testid="${this.props.test.id}"]`);
    if (result) result.focus();
    this.revealOptions();
  }

  formatLimitsText(min, max) {
    if (min && max) {
      return <span className="limits">{`${min} – ${max}`}</span>
    } else if (min) {
      return <span className="limits">{`min ${min}`}</span>
    } else if (max) {
      return <span className="limits">{`max ${max}`}</span>
    } else {
      return null
    }
  }

  calcRangeIndicator(result, min, max) {
    result = parseFloat(result === undefined ? this.props.test.result : result);
    min = parseFloat(min === undefined ? this.props.test.min : min);
    max = parseFloat(max === undefined ? this.props.test.max : max);

    let direction = null;
    let severity = null;
    let color = null;

    if (Number.isFinite(result) && (Number.isFinite(min) || Number.isFinite(max))) {
      if (Number.isFinite(max) && result > max) {
        direction = 'high';
        severity = result > max + Math.abs(max) * 0.1 ? 'severe' : 'slight';
      } else if (Number.isFinite(min) && result < min) {
        direction = 'low';
        severity = result < min - Math.abs(min) * 0.1 ? 'severe' : 'slight';
      } else {
        color = '#22c55e';
      }

      if (severity) {
        color = severity === 'severe' ? '#e11d48' : '#f59e0b';
      }
    }

    this.setState({ rangeIndicator: { direction, severity, color } });
  }

  calcResultFontSize() {
    let length = `${this.props.test?.result}`.length - 5;
    let fontSize = 20;

    if (length > 0) {
      fontSize = fontSize - (4 + length*1.4);
    }

    return fontSize
  }

  buildScaffold(patch) {
    let update = {...this.props.test, ...patch, synced: false};
    let crumbs = update.breadcrumbs?.split('.');
  
    if (crumbs) { // report update sent breadcrumb trail with update obj so we need to build the report structure to merge with existing report
      let reportId = crumbs.reverse().pop();

      let reportStructure = {
        id: reportId,
        ...crumbs.reduce((val, key) => ({[key]: val}), {...update})
      };

      return reportStructure;
    }
  }

  persist(patch, storage, onComplete) {
    if (this.props.saveNode) {
      return this.props.saveNode(
        {...this.props.test, ...patch, synced: false},
        storage,
        onComplete
      );
    }

    return this.props.updateReport(this.buildScaffold(patch), storage, onComplete);
  }

  handlePress() {
    this.focusInput();
    // lol
  }

  getLastChanged() {
    return this.props.test.history?.filter(r => r.result === 1).sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date || 'Not Recorded';
  }

  limitFlip(min, max) {
    this.persist(
      {min: max, max: min}, 
      {fridge: true, freezer: this.props.authToken},
      () => this.calcRangeIndicator(this.props.test.result, max, min)
    )
  }

  render() {
    // console.log('rendering test', this.props.test);

    if (this.props.test.deleted) {return null};
    
    if (this.props.test.type === 'checkbox') {
      return (
        <div
          className="node-container" 
          data-id={this.props.test.id} 
          data-test={this.props.test.testId} 
        >
          <div
            className={`report-node checkbox-node${this.props.test.result ? ' is-checked' : ''}`}
            role="checkbox"
            aria-checked={this.props.test.result === true}
            onClick={() => {
              this.persist(
                {result: !this.props.test.result}, 
                {fridge: true, freezer: this.props.authToken},
              )
            }}
          >

            {!this.props.test.synced && <div className="saving-flash shown" />}
            <div className={`saved-indicator ${this.props.test.synced && this.state.showSavedIndicator ? 'shown' : ''}`}>
              <CheckIcon style={{fontSize: 18}} />
            </div>

            <div className="node-test">
              <div className="test-field">
                <span className="test-name">{this.props.test.test_name}</span>
                <span className="additional">Last changed {this.getLastChanged()}</span>
              </div>
  
              <div className="result-container">
                <span className="result-check" aria-hidden="true">
                  <CheckIcon />
                </span>
              </div>
            </div>

            <div className={`delete-node ${this.state.showDelete ? 'shown' : ''}`}>
              <div 
                className="delete-text"
                onClick={(e) => {
                  e.stopPropagation(); 
                  this.persist(
                    {deleted: true}, 
                    {fridge: true, freezer: this.props.authToken},
                    () => this.setState({showDelete: false})
                  ); 
                }}
              >
                Delete?
              </div>
              <DeleteIcon />
              <div className="never-mind" onClick={(e) => {e.stopPropagation(); this.setState({showDelete: false})}}>Never mind</div>
            </div>
          </div>
        </div>
      )
    } else {
      const calculated = this.isCalculated();
      const calc = calculated ? this.props.test.calc : null;
      const calcValues = calc ? calcInputValues(this.props.test, this.props.report) : {};
      const calcResult = calculated ? this.calcResult() : null;

      return (
        <div
          className="node-container"
          data-id={this.props.test.id}
          data-test={this.props.test.testId}
        >
          <div className={`report-node${this.isNodeExpanded() ? ' is-expanded' : ''}${calculated ? ' calculated-node' : ''}`} onClick={() => this.handlePress()}>
            {!this.props.test?.synced && !this.state.editing && <div className="saving-flash shown" />}

            <div className={`saved-indicator ${this.state.showSavedIndicator ? 'shown' : ''}`}>
              <CheckIcon style={{fontSize: 18}} />
            </div>

            <div className="node-test">
              <div className="test-field">
                <div className="test-title-line">
                  <span className="test-name">{this.props.test.test_name}</span>
                  {calculated ? (
                    <span className="calc-badge" title={calc ? `Calculated: ${calc.formula}` : 'Calculated'}>
                      <FxIcon />
                    </span>
                  ) : null}
                  {this.formatLimitsText(this.props.test.min, this.props.test.max)}
                </div>
                {this.props.test.unit_of_measure ? (
                  <div className="test-unit">
                    <div className="test-unit-inner">
                      <span className="additional">{this.props.test.unit_of_measure}</span>
                    </div>
                  </div>
                ) : null}
              </div>
  
              <div
                className="result-container"
                style={{ '--range-color': this.state.rangeIndicator?.color || '#c5cdd8' }}
              >
                {this.state.rangeIndicator?.direction ? (
                  <span
                    className={`range-caret range-caret--${this.state.rangeIndicator.direction}`}
                    title={`${this.state.rangeIndicator.direction === 'high' ? 'Above' : 'Below'} range`}
                  >
                    {this.state.rangeIndicator.direction === 'high' ? '▲' : '▼'}
                  </span>
                ) : null}
                {calculated ? (
                  <span
                    className={`result calculated-result${calcResult === null ? ' is-blank' : ''}`}
                    testid={this.props.test.id}
                    title={calcResult === null ? 'Waiting on an input' : 'Calculated from the inputs below'}
                  >
                    {calcResult === null ? '—' : calcResult}
                  </span>
                ) : (
                <input
                  testid={this.props.test.id}
                  className="result"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9.\-]+"
                  value={this.props.test.result ?? ''}
                  style={{fontSize: this.calcResultFontSize()}}
                  onChange={(e) => {
                    this.setState({valueChanged: true});
                    this.persist(
                      {result: this.validateResult(e.target.value)}, 
                      {fridge: true},
                      () => this.calcRangeIndicator(this.validateResult(e.target.value))
                    )
                  }}
                  onFocus={() => {
                    this.setState({editing: true});
                    this.revealOptions();
                  }}
                  onBlur={(e) => {
                    if (this.state.valueChanged) {
                      this.setState({editing: false, valueChanged: false});
                      this.persist(
                        {result: e.target.value}, 
                        {freezer: this.props.authToken}
                      )
                    } else {
                      this.setState({editing: false});
                    }
                  }}
                  tabindex={this.props.tabIndex}
                />
                )}
                <span className="width-calc"></span>
              </div>
            </div>

            {calculated ? (
              <div className="calc-inputs" onClick={(e) => e.stopPropagation()}>
                {!calc ? (
                  <span className="calc-input-from">Setting up the calculation…</span>
                ) : calc.inputs.map((input) => {
                  const value = calcValues[input.key];
                  const missing = value === null || value === undefined || `${value}`.trim() === '';
                  return (
                    <label key={input.key} className={`calc-input${missing ? ' is-missing' : ''}`}>
                      <span className="calc-input-text">
                        <span className="calc-input-label">{input.label}</span>
                      </span>
                      {input.source === 'entered' ? (
                        <input
                          className="calc-input-value browser-default"
                          type="text"
                          inputMode="decimal"
                          placeholder={input.default != null ? `${input.default}` : 'Enter'}
                          title={input.default != null ? `Standard value ${input.default}. Type a reading to use it instead.` : undefined}
                          value={typedEntry(this.props.test, input)}
                          onFocus={() => {
                            this.setState({editing: true});
                            this.revealOptions();
                          }}
                          onChange={(e) => this.setEnteredInput(input.key, this.validateResult(e.target.value), {fridge: true})}
                          onBlur={(e) => {
                            this.setState({editing: false});
                            if (`${e.target.value}` !== `${savedEntry(input)}`) {
                              this.setEnteredInput(input.key, e.target.value, {freezer: this.props.authToken});
                            }
                          }}
                        />
                      ) : (
                        <span className="calc-input-value is-linked">{missing ? '—' : value}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            ) : null}

            <div className={`node-reveal${this.props.test.note && !this.state.showNoteInput ? ' shown' : ''}`}>
              <div className="node-reveal-inner">
                <div className="test-note">
                  {/* <span className="test-panel-kicker">Note</span> */}
                  <span className="test-note-text">{this.props.test.note}</span>
                </div>
              </div>
            </div>

            <div
              className={`options${this.state.optionsShown ? ' shown' : ''}`}
              data-id={this.props.test.id}
              aria-hidden={!this.state.optionsShown}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="options-inner">
                <div className="options-bar">
                  {this.props.test.history?.length ? (
                    <button
                      className={`node-option result-history-toggle${this.state.showHistory ? ' is-on' : ''}`}
                      aria-pressed={this.state.showHistory}
                      onClick={() => this.showOption('showHistory')}
                    >
                      <TimelineIcon />
                      Trend
                    </button>
                  ) : null}
                  {/* Operators can't change limits or remove tests; their layout follows the template */}
                  {!this.props.user?.operator && (
                    <button
                      className={`node-option limits-edit-toggle${this.state.showLimitsEditInputs ? ' is-on' : ''}`}
                      aria-pressed={this.state.showLimitsEditInputs}
                      onClick={() => this.showOption('showLimitsEditInputs')}
                    >
                      <SettingsEthernetIcon />
                      Limits
                    </button>
                  )}
                  {/* Where a calculated test's inputs come from is set per system, by staff */}
                  {calc && !this.props.user?.operator && (
                    <button
                      className={`node-option calc-sources-toggle${this.state.showSources ? ' is-on' : ''}`}
                      aria-pressed={this.state.showSources}
                      onClick={() => this.showOption('showSources')}
                    >
                      <AccountTreeOutlinedIcon style={{ transform: 'scaleX(-1)' }} />
                      Inputs
                    </button>
                  )}
                  <button
                    className={`node-option notes-toggle${this.state.showNoteInput ? ' is-on' : ''}`}
                    aria-pressed={this.state.showNoteInput}
                    onClick={() => this.showOption('showNoteInput')}
                  >
                    <ChatIcon />
                    Note
                  </button>
                  {!this.props.user?.operator && (
                    <button
                      className="node-option delete-trigger"
                      onClick={() => this.setState({showDelete: true})}
                    >
                      <DeleteIcon />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
  
            {this.state.showDelete ? (
              <div className={`delete-node shown`}>
                <div 
                  className="delete-text"
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    this.persist(
                      {deleted: true},
                      {fridge: true, freezer: this.props.authToken},
                      () => this.setState({showDelete: false})
                    ); 
                  }}
                >
                  Delete?
                </div>
                <DeleteIcon />
                <div className="never-mind" onClick={(e) => {e.stopPropagation(); this.setState({showDelete: false})}}>Never mind</div>
              </div>
            ) : null}

            <div className={`node-reveal${this.state.showNoteInput ? ' shown' : ''}`}>
              <div className="node-reveal-inner">
                <div className="test-panel test-note-editor" onClick={(e) => {e.stopPropagation(); e.preventDefault();}}>
                  <TextareaAutosize
                    className="test-note-input"
                    minRows={2}
                    onFocus={() => {this.setState({editing: true});}}
                    placeholder="Add a note about this result"
                    value={this.props.test.note}
                    onBlur={(e) => {
                      this.setState({editing: false});
                      this.persist(
                        {note: e.target.value}, 
                        {fridge: true, freezer: this.props.authToken},
                        () => this.setState({showNoteInput: false})
                      );
                    }}
                    onChange={(t) => this.persist({note: t.target.value}, {fridge: true})}
                  />
                  <button
                    className="test-panel-done"
                    type="button"
                    aria-label="Save note"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.persist(
                        {note: this.props.test.note}, 
                        {freezer: this.props.authToken},
                        this.setState({showNoteInput: false})
                      );
                    }}
                  >
                    <CheckIcon />
                  </button>
                </div>
              </div>
            </div>

            <div className={`node-reveal${this.state.showLimitsEditInputs ? ' shown' : ''}`}>
              <div className="node-reveal-inner">
                <div className="test-panel test-limits">
                  <label className="test-limits-field">
                    <span className="test-panel-kicker">Min</span>
                    <input
                      onClick={(e) => {e.stopPropagation(); e.preventDefault();}}
                      value={this.props.test.min ?? ''}
                      onFocus={() => {this.setState({editing: true});}}
                      onChange={(e) => this.persist(
                        {min: this.validateResult(e.target.value)}, 
                        {fridge: true},
                        () => this.calcRangeIndicator(undefined, e.target.value, undefined)
                      )}
                      onBlur={(e) => {
                        this.setState({editing: false})
                        this.persist(
                          {min: e.target.value}, 
                          {freezer: this.props.authToken}
                        )
                      }}
                    />
                  </label>
                  <label className="test-limits-field">
                    <span className="test-panel-kicker">Max</span>
                    <input
                      onClick={(e) => {e.stopPropagation(); e.preventDefault();}}
                      onFocus={() => {this.setState({editing: true});}}
                      value={this.props.test.max ?? ''}
                      onChange={(e) => this.persist(
                        {max: this.validateResult(e.target.value)}, 
                        {fridge: true},
                        () => this.calcRangeIndicator(undefined, undefined, e.target.value)
                      )}
                      onBlur={(e) => {
                        this.setState({editing: false})
                        this.persist(
                          {max: e.target.value}, 
                          {freezer: this.props.authToken}
                        )
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="test-limits-flip"
                    title="Swap min and max"
                    onClick={() => this.limitFlip(this.props.test.min, this.props.test.max)}
                  >
                    <LoopIcon />
                  </button>
                </div>
              </div>
            </div>

            {this.state.showSources && calc ? (
              <div className="node-reveal shown">
                <div className="node-reveal-inner">
                  <CalcSourcesEditor
                    test={this.props.test}
                    report={this.props.report}
                    component={this.props.component}
                    onSave={(sources) => this.props.saveCalcSources(this.props.report.id, this.props.test.id, sources, this.props.authToken)}
                    close={() => this.setState({showSources: false})}
                  />
                </div>
              </div>
            ) : null}

            {this.state.historyMounted ? (
              <div className={`node-reveal${this.state.showHistory ? ' shown' : ''}`}>
                <div className="node-reveal-inner">
                  <ReportTestHistory test={this.props.test} onsite={this.props.report?.date_of_visit}/>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )
    }
  }
}

function mapStateToProps(state, props) {
  // Only select the specific test data needed, not the entire report
  const test = state.report?.components?.[props.component?.id]?.tests?.[props.test?.id];
  
  const current = test || props.test; // Fallback to props.test if not in state

  return {
    authToken: state.authToken,
    report: state.report,
    test: current,
    // A calculated test re-renders when a reading it depends on changes,
    // wherever on the report that reading is.
    calcKey: current?.type === 'calculated' && current.calc ? JSON.stringify(calcInputValues(current, state.report)) : null,
    user: state.user
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

const MemoizedReportTest = React.memo(ReportTest, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.test?.id === nextProps.test?.id &&
    prevProps.test?.result === nextProps.test?.result &&
    prevProps.test?.note === nextProps.test?.note &&
    prevProps.test?.synced === nextProps.test?.synced &&
    prevProps.test?.min === nextProps.test?.min &&
    prevProps.test?.max === nextProps.test?.max &&
    prevProps.test?.calc === nextProps.test?.calc &&
    prevProps.calcKey === nextProps.calcKey &&
    prevProps.synced === nextProps.synced &&
    prevProps.component?.id === nextProps.component?.id
  );
});
export default connect(mapStateToProps, mapDispatchToProps)(MemoizedReportTest);
